import React from "react";
import { Box, Card, CardContent, Grid, Typography, makeStyles } from "@material-ui/core";
import { useTranslation } from "react-i18next";

const useStyles = makeStyles((theme) => ({
  card: {
    marginBottom: theme.spacing(3),
    borderRadius: 8,
  },
  sectionTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(2),
  },
  detailLabel: {
    fontWeight: 600,
    color: "#666",
    fontSize: 12,
    textTransform: "uppercase",
    marginBottom: 4,
  },
}));

interface ScheduledServiceJobSheetProps {
  service: any;
}

export const getJobSheetItems = (service: any) => {
  const vehicleItems = service?.vehicleService?.jobSheet || [];
  const rootItems = (service?.jobSheet || []).map((item: any) => ({
    id: item.id,
    name: item.name || item.task,
    description: item.description || item.taskDescription,
    cost: item.cost ?? 0,
  }));

  if (vehicleItems.length >= rootItems.length) {
    return vehicleItems;
  }
  return rootItems;
};

export const getJobSheetTotal = (service: any, items: any[]) => {
  if (service?.vehicleService?.totalExpenses != null) {
    return (service.vehicleService.totalExpenses || 0) / 100;
  }
  return items.reduce((sum: number, item: any) => sum + ((item.cost || 0) / 100), 0);
};

export const ScheduledServiceJobSheet: React.FC<ScheduledServiceJobSheetProps> = ({ service }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const items = getJobSheetItems(service);
  const jobSheetStatus = service?.vehicleService?.jobSheetStatus;

  if (!items.length) {
    return null;
  }

  const total = getJobSheetTotal(service, items);

  return (
    <Card className={classes.card}>
      <CardContent>
        <Typography variant="h6" className={classes.sectionTitle}>
          {t("root:jobSheet", { defaultValue: "Job sheet" })}
        </Typography>
        {jobSheetStatus && (
          <Typography variant="body1" style={{ marginTop: 8, marginBottom: 16 }}>
            {t("root:status", { defaultValue: "Status" })} :{" "}
            <span
              style={{
                color:
                  jobSheetStatus === "APPROVED"
                    ? "green"
                    : jobSheetStatus === "REJECTED"
                      ? "red"
                      : "orange",
              }}
            >
              {jobSheetStatus === "APPROVED"
                ? t("root:approved", { defaultValue: "Approved" })
                : jobSheetStatus === "REJECTED"
                  ? t("root:declined", { defaultValue: "Rejected" })
                  : t("root:pending", { defaultValue: "Pending" })}
            </span>
          </Typography>
        )}
        {items.map((item: any, index: number) => (
          <Box key={index} p={2} mb={1} bgcolor="#f9f9f9" borderRadius={8}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={6}>
                <Typography variant="body1" style={{ fontWeight: 600 }}>
                  {item.name || item.task}
                </Typography>
                {(item.description || item.taskDescription) && (
                  <Typography variant="body2" color="textSecondary">
                    {item.description || item.taskDescription}
                  </Typography>
                )}
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography className={classes.detailLabel}>
                  {t("root:cost", { defaultValue: "Cost" })}
                </Typography>
                <Typography>£{((item.cost || 0) / 100).toFixed(2)}</Typography>
              </Grid>
            </Grid>
          </Box>
        ))}
        <Box mt={2} p={2} bgcolor="#e3f2fd" borderRadius={8}>
          <Typography variant="h6" align="right">
            {t("root:totalCost", { defaultValue: "Total Cost" })}: £{total.toFixed(2)}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

export default ScheduledServiceJobSheet;
